import { useSelector } from 'react-redux';
import {
  HazardFormField,
  SDSInfo,
  TransportInfo,
  TransportRequirements,
} from 'interface';
import { TranslateField } from '.';
import {
  getComponentStructureData,
  getSdsComponent,
} from 'components/sds-authoring/helper';

export type OriginalString = string | { [key: string]: string } | null;
type Section14Key = 'imdg' | 'iata' | 'adr' | 'rid' | 'adn';

type ReturnType = {
  originalString: OriginalString;
};

export const useOriginalString = (
  fieldName?: TranslateField | null,
  sectionFields?: Array<TranslateField> | null,
  componentId?: number | string | null
): ReturnType => {
  const sdsInfo: SDSInfo | null = useSelector(
    (state: any) => state.pageReducer.sdsInfo
  );
  const SECTION_14_KEY = ['imdg', 'iata', 'adr', 'rid', 'adn'];

  const needTranslated = (value?: string | null): boolean => {
    if (!value) return false;
    try {
      const checkedinputValue = String(value).replace(/<[^>]*>/g, '');
      return !!checkedinputValue.match(/\p{L}+/gu);
    } catch (e) {
      console.log('Error converting original string: ', e);
    }
    return false;
  };

  const getOriginalString = (field?: string): string | null => {
    let checkField = fieldName ?? field;
    if (!checkField) return null;
    if (!sdsInfo) return null;
    if (!sdsInfo.original_data) return null;

    let value: string | null = null;
    let nestedKey = null;
    let fieldKey = null;
    let arrayIndex = null;
    let arrayMatch = checkField.match(/\[\d+\]/);
    if (arrayMatch && arrayMatch.length > 0) {
      arrayIndex = Number(arrayMatch[0].replace(/[\[\]]/g, ''));
      const convertedField = checkField.replace(/\[\d+\]/, '');
      const splittedField = convertedField.split('.');
      if (splittedField.length > 1) {
        nestedKey = splittedField[0];
        fieldKey = splittedField[1];
      }
    }

    /* Original data is SdsInfo interface as default */
    let originalData: { [key: string]: any } | null = sdsInfo.original_data;
    if (nestedKey === 'ingredients') {
      /* For ingredients form, original_data is SdsComponent interface */
      if (!fieldKey || arrayIndex === null) return null;
      const ingredientId = sdsInfo.components?.[arrayIndex]?.id;
      originalData = getSdsComponent(sdsInfo.original_data, ingredientId);
      if (!originalData || !fieldKey) return null;
      arrayMatch = null;
      checkField = fieldKey;
    } else if (componentId) {
      /* For component structured data form, original_data is ComponentStructuredData interface */
      originalData = getComponentStructureData(
        sdsInfo.original_data,
        componentId
      ) as { [key: string]: any };
      if (!originalData) return null;
    }

    /* Check for section 14 Form */
    let section14Key: Section14Key | '' = '';
    let section14KeyField = '';
    if (!componentId) {
      const splittedField = checkField.split('_');
      if (
        splittedField.length > 1 &&
        SECTION_14_KEY.includes(splittedField[0])
      ) {
        section14Key = splittedField[0] as Section14Key;
        section14KeyField = splittedField
          .filter((_, index) => index > 0)
          .join('_');
      }
    }

    if (arrayMatch) {
      if (!nestedKey || !fieldKey || arrayIndex == null) return null;
      if (
        nestedKey in originalData &&
        originalData[nestedKey].length > arrayIndex &&
        originalData[nestedKey][arrayIndex] &&
        fieldKey in originalData[nestedKey][arrayIndex]
      )
        return originalData[nestedKey][arrayIndex][fieldKey];
    } else if (
      (!!section14Key && !!section14KeyField) ||
      (originalData.transport_requirements &&
        (checkField as keyof TransportRequirements) in
          originalData.transport_requirements)
    ) {
      const transportRequirements = originalData.transport_requirements;

      if (
        transportRequirements &&
        (checkField as keyof TransportRequirements) in transportRequirements
      ) {
        value = transportRequirements[
          checkField as keyof TransportRequirements
        ] as string;
      }

      if (
        transportRequirements &&
        section14Key &&
        transportRequirements[section14Key] &&
        section14KeyField &&
        section14KeyField in transportRequirements[section14Key]
      ) {
        value =
          transportRequirements[section14Key][
            section14KeyField as keyof TransportInfo
          ];
      }
    } else if (
      originalData.physical_chemical_properties &&
      (checkField as string) in originalData.physical_chemical_properties
    ) {
      value = originalData.physical_chemical_properties[checkField as string];
    } else if (
      originalData.hazards_information &&
      (checkField as keyof HazardFormField) in originalData.hazards_information
    ) {
      value = originalData.hazards_information[
        checkField as keyof HazardFormField
      ] as string;
    } else if (checkField in originalData) {
      value = originalData[checkField as keyof SDSInfo];
    }

    if (!needTranslated(value)) return null;
    return value;
  };

  const getSectionFieldString = (): { [key: string]: string } | null => {
    if (!sectionFields || !sdsInfo) return null;

    let needTranslated = false;
    const sectionFieldString: { [key: string]: string } = {};
    sectionFields.forEach(field => {
      const fieldValue = getOriginalString(field);
      if (fieldValue) {
        needTranslated = true;
        sectionFieldString[field as string] = fieldValue;
      }
    });
    return needTranslated ? sectionFieldString : null;
  };

  const originalString = getOriginalString() ?? getSectionFieldString();

  return { originalString };
};
